/**
 * WebGL utility functions for shader compilation and program creation
 */

/**
 * Creates and compiles a shader from source
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @param {string} source - The shader source code
 * @param {number} type - The shader type (gl.VERTEX_SHADER or gl.FRAGMENT_SHADER)
 * @returns {WebGLShader} The compiled shader
 */
export function createShader(gl, source, type) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(`Shader compile error: ${gl.getShaderInfoLog(shader)}`);
    }

    return shader;
}

/**
 * Creates a WebGL program from vertex and fragment shaders
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @param {string} vertexSource - The vertex shader source code
 * @param {string} fragmentSource - The fragment shader source code
 * @returns {WebGLProgram} The created program
 */
export function createProgram(gl, vertexSource, fragmentSource) {
    const vertexShader = createShader(gl, vertexSource, gl.VERTEX_SHADER);
    const fragmentShader = createShader(gl, fragmentSource, gl.FRAGMENT_SHADER);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(`Program link error: ${gl.getProgramInfoLog(program)}`);
    }

    return program;
}

/**
 * Creates a texture from an image source
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @param {HTMLImageElement|HTMLCanvasElement} source - The image source
 * @returns {WebGLTexture} The created texture
 */
export function createTexture(gl, source) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
}

/**
 * Updates a texture with new image data
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @param {WebGLTexture} texture - The texture to update
 * @param {HTMLImageElement|HTMLCanvasElement} source - The new image source
 */
export function updateTexture(gl, texture, source) {
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
}

/**
 * Creates buffer data for a full-screen quad
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @returns {Object} Object containing position and texcoord buffers
 */
export function createQuadBuffers(gl) {
    const positions = new Float32Array([
        -1, -1,
        1, -1,
        -1, 1,
        -1, 1,
        1, -1,
        1, 1
    ]);

    const texcoords = new Float32Array([
        0, 1,
        1, 1,
        0, 0,
        0, 0,
        1, 1,
        1, 0
    ]);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const texcoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, texcoords, gl.STATIC_DRAW);

    return {
        position: positionBuffer,
        texcoord: texcoordBuffer
    };
}

/**
 * Sets up vertex attributes for a shader program
 * @param {WebGLRenderingContext} gl - The WebGL context
 * @param {WebGLProgram} program - The shader program
 * @param {Object} buffers - Object containing the buffers to bind
 * @param {Object} attributes - Object mapping attribute names to sizes
 */
export function setupAttributes(gl, program, buffers, attributes) {
    Object.entries(attributes).forEach(([name, size]) => {
        const location = gl.getAttribLocation(program, name);
        if (location !== -1 && buffers[name]) {
            gl.bindBuffer(gl.ARRAY_BUFFER, buffers[name]);
            gl.enableVertexAttribArray(location);
            gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
        }
    });
} 